import React from "react"

import type { ChartDataPoint } from "../types/chart"
import type {
  AggregationType,
  DashboardWidget,
  DashboardWidgetConfig,
  DataSource,
} from "../types/core"
import type { WidgetDateRange } from "../types/dateRange"
import type { DisplayNameLookups } from "../utils/chart/displayNameUtils"
import type { TableRowData } from "./TableWidgetTable"

export type WidgetExportData =
  | {
      widgetType: "table"
      tableData?: {
        rows: TableRowData[]
        columns: {
          key: string
          label: string
          sortable: boolean
        }[]
      }
      dateRange?: { start?: Date; end?: Date }
    }
  | {
      widgetType: "chart"
      chartData: ChartDataPoint[]
      splitKeys: string[]
      displayNameLookups: DisplayNameLookups | null
      dateRange?: { start?: Date; end?: Date }
    }

export interface WidgetProps {
  widget: DashboardWidget & {
    onConfigChange?: (newConfig: DashboardWidgetConfig) => void
  }
  onExportDataReady?: (data: WidgetExportData | null) => void
}

export interface BaseWidgetConfig {
  title?: string
  description?: string
}

export type { DashboardWidgetConfig }

// Shared interface for metric-based widgets
export interface MetricWidgetConfig extends BaseWidgetConfig {
  metricId?: string // Keep for backward compatibility
  metricIds?: string[] // New multi-metric support
  dataSource?: DataSource
  monitoring?: string // @deprecated — use dataSource
  aggregation?: AggregationType
  dateRange?: WidgetDateRange
  bucketSize?: number // Bucket size in milliseconds for time aggregation
}

export abstract class BaseWidget {
  abstract type: string
  abstract title: string
  abstract description: string
  abstract icon: React.ReactNode
  abstract disabled: boolean

  // Optional method for widgets to specify custom minimum dimensions
  getMinimumDimensions?(): { minW?: number; minH?: number } {
    return {}
  }

  // Render the main widget content
  abstract renderWidget(props: WidgetProps): React.ReactNode

  // Render preview for widget selection
  renderPreview(): React.ReactNode {
    return (
      <div className="w-full h-16 bg-surface-1 rounded flex items-center justify-center">
        {this.icon}
      </div>
    )
  }

  // Get default configuration including common fields
  getDefaultConfig(): DashboardWidgetConfig {
    return {
      title: this.title,
      description: "",
    }
  }

  // Get widget template for widget selection
  getTemplate() {
    return {
      type: this.type,
      title: this.title,
      icon: this.icon,
      description: this.description,
      disabled: this.disabled,
    }
  }
}
